<template>
  <div v-if="hasMaterias" class="mt-4 weekly-calendar-section hidden-mobile">
    <div class="weekly-calendar">
      <div class="calendar-header">
        <div class="time-column-header"></div>
        <div class="day-header" v-for="day in weekDays" :key="day.short">
          {{ day.short }}
        </div>
      </div>
      <div class="calendar-body">
        <div class="time-row" v-for="time in timeSlots" :key="time">
          <div class="time-label">{{ time }}</div>
          <div 
            v-for="day in weekDays" 
            :key="`${day.full}-${time}`" 
            class="calendar-cell"
          >
            <div 
              v-if="isScheduleStartTime(day.full, time)" 
              class="schedule-block"
              :style="{ 
                backgroundColor: getScheduleColor(day.full, time),
                ...getScheduleVerticalPosition(day.full, time)
              }"
            >
              <div class="schedule-course">{{ getScheduleCourseName(day.full, time) }}</div>
              <div class="schedule-time">{{ getScheduleTimeRange(day.full, time) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRandomColor } from '@/constants/colors'
import { formatSubjectName, formatFullCourseName } from '@/utils/subjectNameUtils'

export default {
  name: 'WeeklyCalendar',
  props: {
    selectedCourses: { type: Array, default: () => [] },
    leftColumnMaterias: { type: Array, default: () => [] },
    rightColumnMaterias: { type: Array, default: () => [] },
    readOnly: { type: Boolean, default: false }
  },
  data() {
    return {
      weekDays: [
        { short: 'LUN', full: 'Lunes' },
        { short: 'MAR', full: 'Martes' },
        { short: 'MIÉ', full: 'Miércoles' },
        { short: 'JUE', full: 'Jueves' },
        { short: 'VIE', full: 'Viernes' },
        { short: 'SÁB', full: 'Sábado' }
      ],
      timeSlots: ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00']
    }
  },
  computed: {
    hasMaterias() {
      const hasData = (this.leftColumnMaterias || []).length + (this.rightColumnMaterias || []).length > 0;

      return hasData;
    }
  },
  methods: {
    findCourseById(courseId) {
      // Buscar en las materias agrupadas
      for (const materia of [...this.leftColumnMaterias, ...this.rightColumnMaterias]) {
        // Buscar el curso por su id dentro del array courses
        if (materia.courses && Array.isArray(materia.courses)) {
          const course = materia.courses.find(c => String(c.id) === String(courseId));
          if (course) return course;
        }
        // Si la materia tiene allSchedules, reconstruir el curso
        if (materia.allSchedules && Array.isArray(materia.allSchedules) && materia.allSchedules.length > 0) {
          const firstSchedule = materia.allSchedules[0];
          if (String(firstSchedule.courseId) === String(courseId)) {
            return {
              id: firstSchedule.courseId,
              name: firstSchedule.courseName,
              Schedules: materia.allSchedules
            };
          }
        }
      }
      return null;
    },
    isScheduleStartTime(day, time) {
      const slotHour = parseInt(time.split(':')[0]);
      const slotMinute = parseInt(time.split(':')[1]);
      const slotStart = slotHour * 60 + slotMinute;
      const slotEnd = slotStart + 60;

      for (const selection of this.selectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        if (schedule.dayOfWeek !== day) continue;
        if (String(course.id) !== String(selection.courseId)) continue;

        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const startMinute = parseInt(schedule.startTime.split(':')[1]);
        const scheduleStart = startHour * 60 + startMinute;

        // Mostrar el bloque si el horario inicia dentro de este slot de hora
        if (scheduleStart >= slotStart && scheduleStart < slotEnd) {
          return true;
        }
      }
      return false;
    },
    getScheduleColor(day, time) {
      const slotHour = parseInt(time.split(':')[0]);
      const slotMinute = parseInt(time.split(':')[1]);
      const slotStart = slotHour * 60 + slotMinute;
      const slotEnd = slotStart + 60;
      for (const selection of this.selectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        if (schedule.dayOfWeek !== day) continue;
        if (String(course.id) !== String(selection.courseId)) continue;
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const startMinute = parseInt(schedule.startTime.split(':')[1]);
        const scheduleStart = startHour * 60 + startMinute;
        if (scheduleStart >= slotStart && scheduleStart < slotEnd) {
          // Usar la misma lógica que el CourseSelector para garantizar colores idénticos
          // Buscar la materia correspondiente al curso para obtener el subjectName correcto
          const materia = this.findMateriaByCourseId(course.id);
          const subjectName = materia ? materia.subjectName : (course.subjectName || course.name || '');
          return this.getRandomColor(`${course.id}-${subjectName}`);
        }
      }
      return '#3691D8';
    },
    getScheduleCourseName(day, time) {
      const slotHour = parseInt(time.split(':')[0]);
      const slotMinute = parseInt(time.split(':')[1]);
      const slotStart = slotHour * 60 + slotMinute;
      const slotEnd = slotStart + 60;
      
      // Buscar el curso específico que coincide exactamente con este horario
      for (const selection of this.selectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        if (schedule.dayOfWeek !== day) continue;
        if (String(course.id) !== String(selection.courseId)) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const startMinute = parseInt(schedule.startTime.split(':')[1]);
        const scheduleStart = startHour * 60 + startMinute;
        
        // Verificar que el horario coincida exactamente
        if (scheduleStart >= slotStart && scheduleStart < slotEnd) {
          // Obtener la materia (Subject) a la que pertenece el curso
          const materia = this.findMateriaByCourseId(course.id);
          const originalSubjectName = materia?.subjectName || course.subjectName || course.name || '';
          
          // Detectar etiquetas basándose en el nombre del curso
          const courseName = course.name || '';
          const hasMention = /MENCIÓN/gi.test(courseName) || /Mención/gi.test(courseName);
          const isElective = /ELECTIVO/gi.test(courseName) || /Electivo/gi.test(courseName);
          const isCommon = /COMÚN/gi.test(courseName) || /Común/gi.test(courseName);
          
          // Crear objeto curso con etiquetas basadas en el nombre
          const courseWithLabels = {
            ...course,
            hasMention: hasMention,
            isElective: isElective,
            isCommon: isCommon
          };
          
          // Usar utilidad compartida para formatear nombre completo
          return formatFullCourseName(originalSubjectName, courseWithLabels);
        }
      }
      return '';
    },
    getScheduleTimeRange(day, time) {
      const slotHour = parseInt(time.split(':')[0]);
      const slotMinute = parseInt(time.split(':')[1]);
      const slotStart = slotHour * 60 + slotMinute;
      const slotEnd = slotStart + 60;
      for (const selection of this.selectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        if (schedule.dayOfWeek !== day) continue;
        if (String(course.id) !== String(selection.courseId)) continue;
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const startMinute = parseInt(schedule.startTime.split(':')[1]);
        const scheduleStart = startHour * 60 + startMinute;
        if (scheduleStart >= slotStart && scheduleStart < slotEnd) {
          return `${this.formatTime(schedule.startTime)} - ${this.formatTime(schedule.endTime)}`;
        }
      }
      return '';
    },
    getScheduleGridRow() {
      return '';
    },
    getScheduleVerticalPosition(day, time) {
      // Calcular el offset vertical basado en los minutos de inicio
      for (const selection of this.selectedCourses) {
        const course = this.findCourseById(selection.courseId);
        if (!course) continue;
        const schedule = course.Schedules?.find(s => s.id == selection.scheduleId);
        if (!schedule) continue;
        if (schedule.dayOfWeek !== day) continue;
        
        const startHour = parseInt(schedule.startTime.split(':')[0]);
        const startMinute = parseInt(schedule.startTime.split(':')[1]);
        const endHour = parseInt(schedule.endTime.split(':')[0]);
        const endMinute = parseInt(schedule.endTime.split(':')[1]);
        const currentHour = parseInt(time.split(':')[0]);
        
        if (startHour === currentHour) {
          // Calcular el offset basado en los minutos de inicio
          const offsetPercentage = (startMinute / 60) * 100;
          
          // Calcular la duración total en minutos
          const startTimeInMinutes = startHour * 60 + startMinute;
          const endTimeInMinutes = endHour * 60 + endMinute;
          const durationInMinutes = endTimeInMinutes - startTimeInMinutes;
          
          // Calcular cuántas celdas completas ocupa
          const fullCells = Math.floor(durationInMinutes / 60);
          const remainingMinutes = durationInMinutes % 60;
          
          // Calcular la altura total en píxeles
          const cellHeight = 40; // altura de cada celda
          const fullHeight = fullCells * cellHeight;
          const partialHeight = (remainingMinutes / 60) * cellHeight;
          const totalHeight = fullHeight + partialHeight;
          
          return {
            top: `${offsetPercentage}%`,
            height: `${totalHeight}px`,
            zIndex: 10
          };
        }
      }
      return {};
    },
    formatTime(timeString) {
      if (!timeString) return '';
      return timeString.slice(0, 5);
    },
    getCourseSubjectName(courseId) {
      const course = this.findCourseById(courseId);
      if (course) {
        return course.subjectName || course.name || '';
      }
      return '';
    },
    releaseCourseColor(courseId, subjectName) {
      // Importar el store de colores dinámicamente para evitar dependencias circulares
      import('@/store/courseColorsStore').then(module => {
        const courseColorsStore = module.default;
        courseColorsStore.releaseColor(courseId, subjectName);
      });
    },
    getRandomColor,
    
    // Método para encontrar la materia por courseId
    findMateriaByCourseId(courseId) {
      // Buscar en las materias originales para obtener el subjectName correcto
      for (const materia of [...this.leftColumnMaterias, ...this.rightColumnMaterias]) {
        if (materia.courses) {
          const course = materia.courses.find(c => c.id == courseId);
          if (course) return materia;
        }
      }
      return null;
    },
    
    // Método de debug para verificar colores
    debugColors() {
      // Debug method removed
    },
    
    // Método para formatear nombres de materias (usa utilidad compartida)
    formatSubjectName
  },
  watch: {
    selectedCourses: {
      handler(newCourses, oldCourses) {
        // Liberar colores de cursos que ya no están seleccionados
        if (oldCourses && oldCourses.length > 0) {
          const newCourseIds = new Set(newCourses.map(c => `${c.courseId}-${this.getCourseSubjectName(c.courseId)}`))
          oldCourses.forEach(oldCourse => {
            const oldKey = `${oldCourse.courseId}-${this.getCourseSubjectName(oldCourse.courseId)}`
            if (!newCourseIds.has(oldKey)) {
              // Liberar el color del curso deseleccionado
              const course = this.findCourseById(oldCourse.courseId)
              if (course) {
                const subjectName = course.subjectName || course.name || ''
                this.releaseCourseColor(oldCourse.courseId, subjectName)
              }
            }
          })
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    if (this.selectedCourses && this.selectedCourses.length > 0) {
      this.selectedCourses.forEach((selection) => {
        const course = this.findCourseById(selection.courseId);
        if (course) {
          course.Schedules?.find(s => s.id == selection.scheduleId);
        }
      });
    }
  },
  beforeUnmount() {
    // Limpiar todos los colores asignados cuando se desmonte el componente
    import('@/store/courseColorsStore').then(module => {
      const courseColorsStore = module.default;
      courseColorsStore.releaseAllColors();
    });
  }
}
</script>

<style scoped>
.weekly-calendar-section {
  margin-top: 30px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

/* Hide calendar on mobile devices */
.hidden-mobile {
  display: block;
}

@media (max-width: 768px) {
  .hidden-mobile {
    display: none !important;
  }
}

.calendar-header {
  display: grid;
  grid-template-columns: 60px repeat(6, 1fr);
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.time-column-header {
  padding: 12px 8px;
  font-weight: 600;
  color: #333;
  text-align: center;
  border-right: 1px solid #e0e0e0;
}

.calendar-header .day-header {
  padding: 12px 8px;
  font-weight: 600;
  color: #333;
  text-align: center;
  border-right: 1px solid #e0e0e0;
}

.calendar-header .day-header:last-child {
  border-right: none;
}

.calendar-body {
  overflow-y: auto;
  overflow-x: visible;
  display: grid;
  grid-template-columns: 60px repeat(6, 1fr);
  grid-template-rows: repeat(15, 40px);
  position: relative;
}

.time-row {
  display: contents;
}

.time-label {
  padding: 8px;
  font-size: 0.8rem;
  color: #666;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
}

.calendar-cell {
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  min-height: 40px;
  position: relative;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.calendar-cell:nth-child(7n) {
  border-right: none;
}

.time-row:last-child .calendar-cell {
  border-bottom: none;
}

.schedule-block {
  position: absolute;
  padding: 6px 8px;
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 6px;
  margin: 1px;
  z-index: 10;
  left: 2px;
  right: 2px;
  box-sizing: border-box;
  min-height: 20px;
}

.schedule-course {
  font-weight: 100;
  font-family: "Poppins-Medium";
  margin-bottom: 2px;
  line-height: 1.2;
}

.schedule-time {
  font-size: 0.65rem;
  font-family: "Poppins-Medium";
  opacity: 0.9;
  line-height: 1.1;
}

/* ===== RESPONSIVE DESIGN ===== */

/* Mobile (≤768px) */
@media (max-width: 768px) {
  .weekly-calendar-section {
    margin-top: 20px;
    border-radius: 6px;
  }
  
  .calendar-header {
    grid-template-columns: 45px repeat(6, 1fr);
  }
  
  .calendar-body {
    grid-template-columns: 45px repeat(6, 1fr);
    grid-template-rows: repeat(15, 35px);
  }
  
  .time-column-header,
  .calendar-header .day-header {
    padding: 8px 4px;
    font-size: 0.65rem;
  }
  
  .time-label {
    padding: 6px 4px;
    font-size: 0.6rem;
    min-height: 35px;
  }
  
  .calendar-cell {
    min-height: 35px;
  }
  
  .schedule-block {
    padding: 3px 4px;
    font-size: 0.55rem;
    border-radius: 4px;
  }
  
  .schedule-course {
    font-size: 0.55rem;
    margin-bottom: 1px;
    line-height: 1.1;
  }
  
  .schedule-time {
    font-size: 0.5rem;
    line-height: 1.0;
  }
}

/* Mobile Small (320px - 480px) */
@media (max-width: 480px) {
  .weekly-calendar-section {
    margin-top: 16px;
    border-radius: 4px;
  }
  
  .calendar-header {
    grid-template-columns: 40px repeat(6, 1fr);
  }
  
  .calendar-body {
    grid-template-columns: 40px repeat(6, 1fr);
    grid-template-rows: repeat(15, 30px);
  }
  
  .time-column-header,
  .calendar-header .day-header {
    padding: 6px 3px;
    font-size: 0.6rem;
  }
  
  .time-label {
    padding: 4px 3px;
    font-size: 0.55rem;
    min-height: 30px;
  }
  
  .calendar-cell {
    min-height: 30px;
  }
  
  .schedule-block {
    padding: 2px 3px;
    font-size: 0.5rem;
    border-radius: 3px;
  }
  
  .schedule-course {
    font-size: 0.5rem;
    margin-bottom: 1px;
    line-height: 1.1;
  }
  
  .schedule-time {
    font-size: 0.45rem;
    line-height: 1.0;
  }
}

/* Mobile Extra Small (≤320px) */
@media (max-width: 320px) {
  .weekly-calendar-section {
    margin-top: 12px;
  }
  
  .calendar-header {
    grid-template-columns: 35px repeat(6, 1fr);
  }
  
  .calendar-body {
    grid-template-columns: 35px repeat(6, 1fr);
    grid-template-rows: repeat(15, 28px);
  }
  
  .time-column-header,
  .calendar-header .day-header {
    padding: 5px 2px;
    font-size: 0.55rem;
  }
  
  .time-label {
    padding: 3px 2px;
    font-size: 0.5rem;
    min-height: 28px;
  }
  
  .calendar-cell {
    min-height: 28px;
  }
  
  .schedule-block {
    padding: 1px 2px;
    font-size: 0.45rem;
    border-radius: 2px;
  }
  
  .schedule-course {
    font-size: 0.45rem;
    margin-bottom: 1px;
    line-height: 1.1;
  }
  
  .schedule-time {
    font-size: 0.4rem;
    line-height: 1.0;
  }
}

/* Landscape Mobile */
@media (max-height: 500px) and (orientation: landscape) {
  .weekly-calendar-section {
    margin-top: 16px;
  }
  
  .calendar-header {
    grid-template-columns: 40px repeat(6, 1fr);
  }
  
  .calendar-body {
    grid-template-columns: 40px repeat(6, 1fr);
    grid-template-rows: repeat(15, 25px);
  }
  
  .time-label {
    min-height: 25px;
  }
  
  .calendar-cell {
    min-height: 25px;
  }
  
  .schedule-block {
    padding: 2px 4px;
  }
}

/* Tablet (768px - 1023px) */
@media (min-width: 769px) and (max-width: 1023px) {
  .weekly-calendar-section {
    margin-top: 24px;
  }
  
  .calendar-header {
    grid-template-columns: 55px repeat(6, 1fr);
  }
  
  .calendar-body {
    grid-template-columns: 55px repeat(6, 1fr);
    grid-template-rows: repeat(15, 38px);
  }
  
  .time-column-header,
  .calendar-header .day-header {
    padding: 10px 6px;
    font-size: 0.85rem;
  }
  
  .time-label {
    padding: 7px 6px;
    font-size: 0.75rem;
    min-height: 38px;
  }
  
  .calendar-cell {
    min-height: 38px;
  }
  
  .schedule-block {
    padding: 5px 7px;
    font-size: 0.68rem;
  }
  
  .schedule-course {
    font-size: 0.68rem;
  }
  
  .schedule-time {
    font-size: 0.63rem;
  }
}

/* Desktop (1024px+) */
@media (min-width: 1024px) {
  .weekly-calendar-section {
    margin-top: 30px;
  }
  
  .calendar-header {
    grid-template-columns: 60px repeat(6, 1fr);
  }
  
  .calendar-body {
    grid-template-columns: 60px repeat(6, 1fr);
    grid-template-rows: repeat(15, 40px);
  }
  
  .time-column-header,
  .calendar-header .day-header {
    padding: 12px 8px;
    font-size: 0.9rem;
  }
  
  .time-label {
    padding: 8px;
    font-size: 0.8rem;
    min-height: 40px;
  }
  
  .calendar-cell {
    min-height: 40px;
  }
  
  .schedule-block {
    padding: 6px 8px;
    font-size: 0.7rem;
  }
  
  .schedule-course {
    font-size: 0.7rem;
  }
  
  .schedule-time {
    font-size: 0.65rem;
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .weekly-calendar-section {
    border-width: 0.5px;
  }
  
  .calendar-header,
  .calendar-cell {
    border-width: 0.5px;
  }
}

/* Print styles */
@media print {
  .weekly-calendar-section {
    background: white;
    box-shadow: none;
    border: 1px solid #000;
  }
  
  .schedule-block {
    border: 1px solid #000;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .schedule-block {
    transition: none;
  }
}
</style> 